/**
 * Event song upload + playback helpers (Storage path event-audio/{eventId}/…).
 */
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';

export const EVENT_AUDIO_ACCEPT =
  'audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/wav,audio/x-m4a,.mp3,.m4a,.aac,.ogg,.wav';

export const EVENT_AUDIO_MAX_BYTES = 10 * 1024 * 1024;

const ALLOWED_TYPES = new Set([
  'audio/mpeg',
  'audio/mp4',
  'audio/aac',
  'audio/ogg',
  'audio/wav',
  'audio/x-m4a',
]);

/**
 * @param {string|undefined|null} contentType
 * @param {string|undefined|null} fileName
 * @returns {boolean}
 */
export function isAllowedEventAudioType(contentType, fileName = '') {
  const type = String(contentType || '').toLowerCase().trim();
  if (ALLOWED_TYPES.has(type)) return true;
  const name = String(fileName || '').toLowerCase();
  return /\.(mp3|m4a|aac|ogg|wav)$/.test(name);
}

/**
 * @param {Blob|File} audioFile
 * @param {string} eventId
 * @returns {Promise<{ audioUrl: string, audioPath: string, audioContentType: string, audioTitle: string }>}
 */
export async function uploadEventAudio(audioFile, eventId) {
  if (!storage) {
    throw new Error('Firebase Storage not initialized.');
  }
  if (!(audioFile instanceof Blob)) {
    throw new Error('Invalid audio file.');
  }
  if (audioFile.size > EVENT_AUDIO_MAX_BYTES) {
    throw new Error('Audio file must be under 10MB.');
  }
  const fileName =
    (audioFile instanceof File && audioFile.name)
      ? audioFile.name
      : `track-${Date.now()}.mp3`;
  if (!isAllowedEventAudioType(audioFile.type, fileName)) {
    throw new Error('Use mp3, m4a, aac, ogg, or wav.');
  }

  const audioPath = `event-audio/${eventId}/${fileName}`;
  const storageRef = ref(storage, audioPath);
  await uploadBytes(storageRef, audioFile, {
    contentType: audioFile.type || 'audio/mpeg',
  });
  const audioUrl = await getDownloadURL(storageRef);
  const audioTitle = fileName.replace(/\.[^.]+$/, '');

  return {
    audioUrl,
    audioPath,
    audioContentType: audioFile.type || 'audio/mpeg',
    audioTitle,
  };
}

/** Pause every `.event-audio` / hub player node except optionally one keep-playing node. */
export function pauseOtherEventAudio(exceptEl = null) {
  if (typeof document === 'undefined') return;
  // Class owned by src/components/mp3-player (HUB_AUDIO_CLASS). Do not import components from services.
  document.querySelectorAll('audio.event-audio, audio.hub-mp3-audio').forEach((el) => {
    if (el !== exceptEl && !el.paused) {
      el.pause();
    }
  });
}
